import { useState } from 'react'
import { X, FileText, Share2, User, Users } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { TeamMemberSelector, useTeamMemberSelection } from '@/teams'

interface ShareInterviewModalProps {
  open: boolean
  onClose: () => void
  interview: any
  onShared?: () => void
}

export default function ShareInterviewModal({ open, onClose, interview, onShared }: ShareInterviewModalProps) {
  const [shareType, setShareType] = useState<'team' | 'individual'>('team')
  const [individualType, setIndividualType] = useState<'team_member' | 'email'>('team_member')
  const [email, setEmail] = useState('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const [foundUser, setFoundUser] = useState<any>(null)
  const [searchingUser, setSearchingUser] = useState(false)
  
  const {
    selectedTeamId,
    selectedUserId,
    setSelectedTeamId,
    setSelectedUserId,
    resetSelection
  } = useTeamMemberSelection()

  const resetForm = () => {
    setShareType('team')
    setIndividualType('team_member')
    setEmail('')
    setNotes('')
    setFoundUser(null)
    resetSelection()
  }

  const searchUserByEmail = async (emailAddress: string) => {
    if (!emailAddress.trim()) {
      setFoundUser(null)
      return
    }

    try {
      setSearchingUser(true)
      const response = await apiClient.getUsers({ search: emailAddress.trim() })
      
      if (response?.success && (response?.data as any)?.users) {
        const user = (response.data as any).users.find((u: any) => 
          u.email.toLowerCase() === emailAddress.toLowerCase()
        )
        setFoundUser(user || null)
      } else {
        setFoundUser(null)
      }
    } catch (err) {
      console.error('Failed to search user:', err)
      setFoundUser(null)
    } finally {
      setSearchingUser(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!interview?.id) return

    try {
      setLoading(true)
      
      let response
      if (shareType === 'team' && selectedTeamId) {
        response = await apiClient.shareInterviewWithTeam(interview.id, {
          teamId: selectedTeamId as any, // TODO: API should accept UUID
          notes: notes
        })
      } else if (shareType === 'individual') {
        let userIdToShare = selectedUserId
        
        if (individualType === 'email' && foundUser) {
          userIdToShare = foundUser.id
        }
        
        if (!userIdToShare) {
          throw new Error('Please select a team member or provide a valid email address')
        }
        
        response = await apiClient.shareInterviewWithUser(interview.id, {
          userId: userIdToShare as any, // TODO: API should accept UUID
          notes: notes
        })
      } else {
        throw new Error('Please select a team or individual to share with')
      }

      if (response?.success) {
        resetForm()
        onShared?.()
        onClose()
      } else {
        throw new Error(response?.error || 'Failed to share interview')
      }
    } catch (err) {
      console.error('Failed to share interview:', err)
      alert('Failed to share interview: ' + (err instanceof Error ? err.message : 'Unknown error'))
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Share2 className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Share Interview</h2>
              <p className="text-sm text-gray-500">{interview?.title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Share Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Share with
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="team"
                    checked={shareType === 'team'}
                    onChange={(e) => setShareType(e.target.value as 'team' | 'individual')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    Entire Team
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="individual"
                    checked={shareType === 'individual'}
                    onChange={(e) => setShareType(e.target.value as 'team' | 'individual')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 flex items-center">
                    <User className="w-4 h-4 mr-1" />
                    Individual Member
                  </span>
                </label>
              </div>
            </div>

            {/* Individual Type Selection (only when individual is selected) */}
            {shareType === 'individual' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Share method
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="team_member"
                      checked={individualType === 'team_member'}
                      onChange={(e) => setIndividualType(e.target.value as 'team_member' | 'email')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Team Member
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="email"
                      checked={individualType === 'email'}
                      onChange={(e) => setIndividualType(e.target.value as 'team_member' | 'email')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Email Address
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Email Input (only when email type is selected) */}
            {shareType === 'individual' && individualType === 'email' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="space-y-3">
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value)
                        searchUserByEmail(e.target.value)
                      }}
                      placeholder="Enter email address..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                    <User className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  </div>
                  
                  {searchingUser && (
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                      <span>Searching for user...</span>
                    </div>
                  )}
                  
                  {email && !searchingUser && foundUser && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                        <span className="text-sm font-medium text-green-800">User found:</span>
                      </div>
                      <p className="text-sm text-green-700 mt-1">
                        {foundUser.first_name} {foundUser.last_name} ({foundUser.email})
                      </p>
                    </div>
                  )}
                  
                  {email && !searchingUser && !foundUser && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full" />
                        <span className="text-sm font-medium text-red-800">User not found</span>
                      </div>
                      <p className="text-sm text-red-700 mt-1">
                        No user found with this email address in your organization.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Team/Member Selection (only when team or team_member is selected) */}
            {(shareType === 'team' || (shareType === 'individual' && individualType === 'team_member')) && (
              <>
                {console.log('🔍 MODAL DEBUG: Rendering TeamMemberSelector with excludeCurrentUser=true')}
                <TeamMemberSelector
                  selectedTeamId={selectedTeamId}
                  selectedUserId={selectedUserId}
                  onTeamChange={setSelectedTeamId}
                  onUserChange={setSelectedUserId}
                  selectionMode={shareType === 'team' ? 'team' : 'both'}
                  required={true}
                  label={shareType === 'team' ? 'Select Team' : 'Select Team and Member'}
                  placeholder="Choose a team..."
                  excludeCurrentUser={true}
                />
              </>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <div className="relative">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add a note about why you're sharing this interview..."
                  rows={3}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <FileText className="w-5 h-5 text-gray-400 absolute left-3 top-3" />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || 
                  (shareType === 'team' && !selectedTeamId) ||
                  (shareType === 'individual' && individualType === 'team_member' && (!selectedTeamId || !selectedUserId)) ||
                  (shareType === 'individual' && individualType === 'email' && !foundUser)
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Share2 className="w-4 h-4" />
                    <span>
                      Share with {shareType === 'team' ? 'Team' : 'Individual'}
                    </span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}